import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import {
  FiCalendar,
  FiDollarSign,
  FiDownload,
  FiFileText,
  FiLock,
  FiMessageCircle,
  FiShield,
} from 'react-icons/fi';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { Footer, MarketingHeader, SEO } from '@/components';
import { track } from '@/lib/analytics';
import { getAppStoreUrl, getPlayStoreUrl, getUtmParamsFromUrl } from '@/lib/urls';

const FEATURES = [
  {
    icon: FiMessageCircle,
    title: 'Secure co-parent messaging',
    description:
      'Keep every conversation documented in one place. Harbor provides a co-parenting platform with secure messaging so high-conflict exchanges stay on record and out of personal texts.',
  },
  {
    icon: FiDollarSign,
    title: 'Expense tracking & split calculations',
    description:
      'Log child-related expenses, split costs automatically, and maintain a clear ledger. Harbor is a co-parenting expense app built for shared financial accountability.',
  },
  {
    icon: FiCalendar,
    title: 'Custody calendar & time logs',
    description:
      'Manage custody schedules, exceptions, and parenting time in one shared calendar. Harbor works as a custody tracking app with expense and time logs in a single workflow.',
  },
  {
    icon: FiFileText,
    title: 'Receipt scanner with smart tagging',
    description:
      'Snap a photo of any receipt and Harbor extracts the amount, merchant, and date. Attach receipts to expense records for a complete audit trail.',
  },
  {
    icon: FiDownload,
    title: 'One-tap court-ready exports',
    description:
      'Export messages, expenses, and calendar records in seconds. Generate comprehensive reports for attorneys or court filings without manual assembly.',
  },
  {
    icon: FiShield,
    title: 'Court-ready records',
    description:
      'Harbor maintains tamper-evident records with timestamps and audit trails designed for family-law workflows across the US and Canada.',
  },
  {
    icon: FiLock,
    title: 'Private journal',
    description:
      'A personal notebook for medical visits, handoffs, and notes — visible only to you, not shared with your co-parent.',
  },
];

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://harborcoparent.com/' },
    { '@type': 'ListItem', position: 2, name: 'Features', item: 'https://harborcoparent.com/features' },
  ],
};

const itemListJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Harbor Co-Parenting Features',
  itemListElement: FEATURES.map((feature, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: feature.title,
    description: feature.description,
  })),
};

function FeaturesPage() {
  useEffect(() => {
    track('Features Screen');
  }, []);

  const utm = getUtmParamsFromUrl();

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Co-Parenting App Features: Messaging, Expenses & Custody | Harbor'
        description='Harbor is a co-parenting platform with secure messaging, expense tracking, custody calendars, receipt scanning, and court-ready exports. Available on iOS, Android, and web.'
        path='/features'
        jsonLd={[breadcrumbJsonLd, itemListJsonLd]}
      />
      <MarketingHeader />

      <section className='bg-primary/5 pt-32 pb-12'>
        <div className='mx-auto max-w-4xl px-6 text-center'>
          <h1 className='mb-4 text-4xl font-bold text-gray-900 md:text-5xl'>
            Co-parenting platform features
          </h1>
          <p className='mx-auto max-w-2xl text-xl leading-relaxed text-gray-600'>
            Secure messaging, expense tracking, custody scheduling, and court-ready records —
            everything co-parents need in one app. Works solo or with both parents. Available
            in the US and Canada.
          </p>
        </div>
      </section>

      <section className='mx-auto max-w-5xl px-6 py-16'>
        <div className='space-y-12'>
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <article key={feature.title} className='flex gap-6'>
                <div className='flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary/10'>
                  <Icon className='h-7 w-7 text-primary' />
                </div>
                <div>
                  <h2 className='text-2xl font-bold text-gray-900'>{feature.title}</h2>
                  <p className='mt-2 text-lg leading-relaxed text-gray-600'>{feature.description}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className='bg-primary py-14'>
        <div className='mx-auto max-w-4xl px-6 text-center'>
          <h2 className='text-3xl font-bold text-white'>Try Harbor free</h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-white/85'>
            Download Harbor on iOS or Android, or sign up on web.
          </p>
          <div className='mt-8 flex flex-col justify-center gap-3 sm:flex-row'>
            <a
              href={getAppStoreUrl('features-page', utm)}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => track('Click App Store', { location: 'Features' })}
              className='inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-primary hover:bg-slate-100'
            >
              <FaApple size={18} />
              App Store
            </a>
            <a
              href={getPlayStoreUrl('features-page', utm)}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => track('Click Google Play', { location: 'Features' })}
              className='inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-primary hover:bg-slate-100'
            >
              <FaGooglePlay size={18} />
              Google Play
            </a>
            <Link
              to='/auth/sign-up'
              onClick={() => track('Click Sign Up', { location: 'Features' })}
              className='inline-flex items-center justify-center rounded-lg border border-white px-5 py-3 font-semibold text-white hover:bg-white/10'
            >
              Sign up on web
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/features')({
  component: FeaturesPage,
});
